<template>
  <div
    id="general-summay-content"
    class="general-summay-containter"
  >
    <tender-head
      ref="tenderEditHead"
      :tenderGeneral="currentTender.general"
    >
      <start-addendum v-if="isPicView && isIssued" />
      <button
        v-if="allowStartAnalysis"
        class="btn btn--orange nav-btns"
        @click="startAnalysis"
      >{{ $t('action.startAnalysis') }}</button>
    </tender-head>

    <div id="general-summay-accordion-container">
      <Collapse
        class="tender-summary-collapse"
        :title="$t('summary.name')"
        :withBorder="false"
      >
        <template slot="content">
          <div
            id="router-view-content"
          >
            <HKLSelect
              style="width: 330px;"
              :value="componentName ? componentName.target : undefined"
              :options="componentHklOptions"
              :showSearch="false"
              placeholder=""
              @change="onComponentChange"
            />
            <router-view class="router-view-content-detail" />
          </div>
        </template>
      </Collapse>
    </div>
    <detail-summary
      v-if="currentTender.general.id === tenderId"
      :tenderId="tenderId"
    />
    <TenderRepresentative v-show="showTenderRep" />
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'
import Collapse from '@/controls/Collapse'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import DetailSummary from '@/modules/tenders/review/DetailSummary'
import StartAddendum from '@/modules/tenders/addendum/StartAddendum'
import TenderRepresentative from '@/modules/tenders/view/TenderRepresentative'
import TenderHead from '@/modules/tenders/TenderHead'
import tenderRepShowMixin from '@/modules/tenders/tenderRepShowMixin'
import mutationTypes from '@/store/mutation-types'
import navigation from '@/utils/navigation'
import * as tendersApi from '@/api/tenders/request'
import { ActionTypes } from '@/utils/consts'
import { tenderStatus, bidStatusMapHealthCheck } from '@/utils/locale'

export default {
  components: {
    Collapse,
    HKLSelect,
    DetailSummary,
    TenderHead,
    StartAddendum,
    TenderRepresentative,
  },
  mixins: [tenderRepShowMixin],
  async beforeRouteUpdate (to, from, next) {
    if (String(to.params.id) === String(from.params.id)) {
      next()
      return
    }
    try {
      this.tenderId = to.params.id
      await this.loadTender(to.params.id)
      if (!this.validateRoute({ routeName: to.name })) {
        next(false)
        return
      }
      await this.loadRecentRevision({ loadPendingAddendum: true })
      this.componentName = Object.values(this.allOptions).find((rec) => (rec.target === to.name))
      next()
    } catch (err) {
      next(err)
    }
  },
  data () {
    return {
      componentName: null,
      tenderId: '',
    }
  },
  computed: {
    ...mapGetters('tenders', ['currentTender', 'isAddendum']),
    ...mapGetters('user', ['currentUser', 'isEngineer']),
    allOptions () {
      const suffix = this.isEngineer ? '' : 'Ext'
      return {
        tenderHealthcheck: {
          name: this.$t('summary.location.tenderHealthcheck'),
          target: `healthCheck${suffix}`,
        },
        addendum: {
          name: this.$t('summary.location.addendum'),
          target: `addendumSummary${suffix}`,
        },
        analysisRecos: {
          name: this.$t('summary.location.analysisRecos'),
          target: `analysisRecos${suffix}`,
        },
        queryStatus: {
          name: this.$t('summary.location.queryStatus'),
          target: `queryStatus${suffix}`,
        },
      }
    },
    options () {
      const options = [
        ...(deep(this.statusOptions, [this.currentTender.general.status, 'default']) || []),
        ...(deep(this.statusOptions, [this.currentTender.general.status, this.currentTender.general.tenderType]) || []),
      ]
      if (this.isAddendum && !options.includes('addendum')) {
        options.push('addendum')
      }

      if (this.componentName && this.componentName.target === 'queryStatus' && !this.hasQueries) {
        navigation.toPage('analysisRecos', { id: this.tenderId })
        this.componentName = this.allOptions.analysisRecos
      }

      return options.map(
        rec => (this.allOptions[rec]),
      )
    },
    isIssued () {
      return this.currentTender.general.status === tenderStatus.ISSUED
    },
    isReady () {
      return this.currentTender.general.status === tenderStatus.READY
    },
    isClosingDateReached () {
      return +new Date(this.currentTender.general.closingDate) <= +new Date()
    },
    isInAddendum () {
      return tendersApi.isTenderInAddendum(this.currentTender.general, true)
    },
    isPicView () {
      return this.currentUser.id === this.currentTender.general.managedBy
    },
    hasQueries () {
      if (!deep(this.currentTender, 'bids')) return false
      const bids = deep(this.currentTender, 'bids') || []
      return bids.some(bid => bid.queries && bid.queries.length > 0)
    },
    statusOptions () {
      let afterReadyOptions = ['tenderHealthcheck', 'analysisRecos']
      if (this.hasQueries) {
        afterReadyOptions.push('queryStatus')
      }
      return {
        ISSUED: {
          default: ['tenderHealthcheck'],
        },
        READY: {
          default: ['tenderHealthcheck'],
        },
        REVIEW: {
          default: afterReadyOptions,
        },
        REGRETTED: {
          default: afterReadyOptions,
        },
        AWARDED: {
          default: afterReadyOptions,
        },
      }
    },
    allBidsReplied () {
      if (!deep(this.currentTender, 'bids')) return false
      const bids = deep(this.currentTender, 'bids') || []
      return !bids.some(bid => !bidStatusMapHealthCheck[bid.status].trim())
    },
    allowStartAnalysis () {
      return this.isPicView && this.isReady
    },
    componentHklOptions () {
      return this.options.map((opt) => ({
        name: opt.name,
        value: opt.target,
      }))
    },
  },
  async mounted () {
    this.tenderId = this.$router.currentRoute.params.id
    await this.loadTender(this.tenderId)
    if (!this.validateRoute()) return
    await this.loadRecentRevision({ loadPendingAddendum: true })
    this.componentName = Object.values(this.allOptions).find((rec) => (rec.target === this.$router.currentRoute.name))
  },
  methods: {
    ...mapMutations('tenders', {
      updateTenderPathField: mutationTypes.TENDER_UPDATE_PATH_FIELD,
    }),
    ...mapActions('tenders', [
      'updateTenderStatus',
      'loadTender',
      'validateRoute',
      'refreshTender',
      'loadRecentRevision',
    ]),
    navToComponent (name) {
      this.$router.push({
        name,
      })
    },
    onComponentChange (target) {
      const componentName = this.options.find((opt) => opt.target === target)
      this.componentName = componentName || null
      this.navToComponent(target || '')
    },
    async startAnalysis () {
      try {
        this.$root.$children[0].$refs.loadingLayer.$emit('startLoading')
        if (this.isInAddendum) {
          await this.refreshTender()
        }
        await this.updateTenderStatus({
          id: this.tenderId,
          status: tenderStatus.REVIEW,
          action: ActionTypes.tenders.analysis.create,
        })
        await this.refreshTender()
        this.componentName = this.allOptions.analysisRecos
        navigation.toPage('analysisRecos', { id: this.tenderId })
      } finally {
        this.$root.$children[0].$refs.loadingLayer.$emit('stopLoading')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
#general-summay-content {
  ::v-deep a.back {
    font-family: Verdana;
    font-size: 14px;
    color: #0c3a58;
  }
}

#router-view-content {
  padding-top: 10px;
}

.router-view-content-detail {
  padding-top: 20px;
}

.nav-btns {
  float: right;
  margin-left: 15px;
}
#general-summay-accordion-container {
  max-width: 1440px;
  margin: 0 auto;
}

.tender-summary-collapse {
  & ::v-deep .title-container {
    border-bottom: 1px solid #e0e0e0;
    padding: 14px 24px;
    .default-title {
      font-size: 22px;
      line-height: 25px;
      font-weight: normal;
    }
  }
  & ::v-deep .content {
    padding: 0 24px;
  }
}
</style>
